import React, { FC, useState, MouseEvent, useCallback, SyntheticEvent } from 'react';
import { isEmpty, map } from 'lodash';
import { NavMenuVariant, NavMenuItem, Profile, ContactUs, ContactInfo } from '../SideNav';
import { NavMenuElement } from '../SideNav/NavElement';
import { setRelAttribute } from '@trip-ux/common';
import { AUTH_HEADERS, NAV_BAR_USER_ITEMS } from '@trip-ux/constants';

export interface TopNavProps {
  isAuthenticated: boolean;
  profile: Profile;
  navElements?: NavMenuItem[];
  languageToggleLabel: string;
  languageToggleLink: string;
  languageToggleAriaLabel: string;
  onSelect: (navId: string, e: MouseEvent) => void;
  onLanguageToggle?: (e: MouseEvent) => void;
  contactUs?: NavMenuItem;
  contactInfo?: ContactInfo;
  userName?: string;
  userType?: string;
  isAccountDropDisabled?: boolean;
}

const checkProfileAndAuthentication = (profile, isAuthenticated) => {
  return profile && isAuthenticated;
};

export const TopNav: FC<TopNavProps> = ({
  isAuthenticated = false,
  profile,
  navElements,
  languageToggleLabel,
  languageToggleLink,
  languageToggleAriaLabel,
  onSelect,
  onLanguageToggle = () => true,
  contactUs,
  contactInfo,
  userName,
  userType,
  isAccountDropDisabled = false
}) => {
  const [showDrop, setShowDrop] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const onClickDrop = useCallback(
    (navId: string, e: MouseEvent) => {
      showContact && setShowContact(false);
      onSelect(navId, e);
      setShowDrop(!showDrop);
    },
    [showContact, onSelect, showDrop]
  );
  const contactUsCallback = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      showDrop && setShowDrop(false);
      setShowContact(!showContact);
    },
    [showContact, showDrop]
  );
  return (
    <nav className="top-nav d-none d-lg-block" data-testid="top-nav">
      <ul className="d-flex flex-row justify-content-end" role="none">
        {!isEmpty(navElements) &&
          map(navElements, element => (
            <li className="nav-elements" role="none" key={`nav-elements-${element.navId}`}>
              <NavMenuElement
                navId={element.navId}
                ariaLabel={element.ariaLabel}
                label={element.label}
                invertedLogo={element.invertedLogo}
                logo={element.logo}
                className={`nav-drop-element ${NavMenuVariant.HORIZONTAL}`}
                onClick={e => onSelect(element.navId, e)}
                isMobile={true}
                href={element.href}
              />
            </li>
          ))}
        {checkProfileAndAuthentication(profile, isAuthenticated) && (
          <li className="position-relative" role="none" data-testid="nav-ele-auth">
            <NavMenuElement
              navId={profile.authProfile.navId}
              ariaLabel={userName}
              label={userName}
              invertedLogo={profile.authProfile.invertedLogo}
              logo={profile.authProfile.logo}
              className={`nav-drop-element profile ${NavMenuVariant.HORIZONTAL}`}
              showDrop={!isEmpty(profile.authProfile.subMenuList)}
              dropOpen={showDrop}
              onClick={e => onClickDrop(profile.authProfile.navId, e)}
              isMobile={true}
            />
            {showDrop && (
              <ul role="menu" className="position-absolute d-flex flex-column w-100" data-testid="nav-auth-items">
                {!isAccountDropDisabled &&
                  profile.authProfile?.subMenuList.map(item => {
                    if (
                      userType === AUTH_HEADERS.CIAM_ID ||
                      (userType === AUTH_HEADERS.LAN_ID && item.navId === NAV_BAR_USER_ITEMS.USER_LOGOUT)
                    ) {
                      return (
                        <li role="none" key={`${item.navId}-drop-sub`} className="d-flex">
                          <NavMenuElement
                            href={item.href}
                            navId={item.navId}
                            ariaLabel={item.ariaLabel}
                            label={item.label}
                            invertedLogo={item.invertedLogo}
                            logo={item.logo}
                            className={`${NavMenuVariant.HORIZONTAL} profile-drop`}
                            onClick={e => {
                              e.preventDefault();
                              onClickDrop(item.navId, e);
                            }}
                          />
                        </li>
                      );
                    }
                  })}
              </ul>
            )}
          </li>
        )}
        {checkProfileAndAuthentication(profile, !isAuthenticated) && (
          <li className="sign-in" role="none" data-testid="nav-sign-item">
            <a href={profile.signInLink} rel={setRelAttribute(profile.signInLink)} role="menuitem">
              {profile.signInLabel}
            </a>
          </li>
        )}
        <li role="none">
          <a
            className="language-toggle"
            data-testid="language-toggle"
            role="none"
            href={languageToggleLink}
            aria-label={languageToggleAriaLabel}
            onClick={onLanguageToggle}
            rel={setRelAttribute(languageToggleLink)}>
            {languageToggleLabel}
          </a>
        </li>
      </ul>
    </nav>
  );
};

TopNav.displayName = 'TopNav';
export default TopNav;
